import React from 'react';
import classNames from 'classnames';

import css from './CommissionFields.module.css';

/**
 * The four numbers that make up a commission, with a preview of what they mean.
 *
 * Minimums are entered and shown in dollars because that is how they are negotiated.
 * They are converted to cents at the server boundary and nowhere else — money kept in
 * floats is a rounding error waiting to be found in reconciliation.
 *
 * The preview is the point of this component. An administrator setting 15% and a $5
 * minimum cannot be expected to work out what a guest pays and what the provider keeps,
 * and the alternative to showing them is finding out at checkout.
 */

const Row = ({ label, percentage, minimumDollars, disabled, onChange, errors }) => (
  <fieldset className={css.row}>
    <legend className={css.legend}>{label}</legend>
    <div className={css.pair}>
      <label className={css.field}>
        <span className={css.label}>Commission (%)</span>
        <input
          type="number"
          className={css.input}
          min={0.01}
          max={100}
          step="0.01"
          value={percentage}
          disabled={disabled}
          onChange={e => onChange({ percentage: e.target.value })}
        />
      </label>
      <label className={css.field}>
        <span className={css.label}>Minimum ($)</span>
        <input
          type="number"
          className={css.input}
          min={0.01}
          step="0.01"
          value={minimumDollars}
          disabled={disabled}
          onChange={e => onChange({ minimumDollars: e.target.value })}
        />
      </label>
    </div>
    {errors?.length ? (
      <ul className={css.errors}>
        {errors.map(e => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    ) : null}
  </fieldset>
);

/**
 * What the numbers come to on an example price.
 *
 * Deliberately labelled as an estimate: the figures a transaction is actually charged
 * come from the pricing endpoint, and a preview that quietly disagreed with the real
 * charge would be worse than no preview at all.
 */
const Preview = ({ value, examplePrice }) => {
  const price = Number(examplePrice) || 0;
  const side = s => {
    const pct = (Number(s.percentage) || 0) / 100;
    const minimum = Number(s.minimumDollars) || 0;
    return Math.max(price * pct, minimum);
  };

  const providerCut = side(value.providerCommission);
  const customerCut = side(value.customerCommission);
  const money = n => `$${n.toFixed(2)}`;

  return (
    <div className={css.preview}>
      <div className={css.previewRow}>
        <span>Customer pays</span>
        <b>{money(price + customerCut)}</b>
      </div>
      <div className={css.previewRow}>
        <span>Provider receives</span>
        <b>{money(price - providerCut)}</b>
      </div>
      <div className={css.previewRow}>
        <span>Platform commission</span>
        <b>{money(providerCut + customerCut)}</b>
      </div>
      <p className={css.previewNote}>
        An estimate on a {money(price)} listing, before payment processing fees and taxes.
      </p>
    </div>
  );
};

const CommissionFields = props => {
  const { className, value, onChange, disabled, errors = [], examplePrice = 100 } = props;

  const update = (side, patch) =>
    onChange({ ...value, [side]: { ...value[side], ...patch } });

  return (
    <div className={classNames(css.root, className)}>
      <Row
        label="Provider commission — deducted from the listing price"
        percentage={value.providerCommission.percentage}
        minimumDollars={value.providerCommission.minimumDollars}
        disabled={disabled}
        onChange={patch => update('providerCommission', patch)}
      />
      <Row
        label="Customer commission — added to the listing price"
        percentage={value.customerCommission.percentage}
        minimumDollars={value.customerCommission.minimumDollars}
        disabled={disabled}
        onChange={patch => update('customerCommission', patch)}
      />

      {errors.length ? (
        <ul className={css.errors}>
          {errors.map(e => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}

      <Preview value={value} examplePrice={examplePrice} />
    </div>
  );
};

export default CommissionFields;
